/**
 * LoopBack REST API Asset Service & Local Cache.
 * Connects directly to backend API endpoint at /api/assets for Asset model (asset.model.ts).
 */
import { AuthService } from './AuthService';

export interface BackendAsset {
  id?: string;
  name?: string;
  category: 'Graphic' | 'Background' | 'Text' | 'Component';
  type: 'element' | 'component' | 'array' | 'html' | 'image';
  src?: string;
  thumbnail?: string;
  element?: string;
  data?: string;
  dataType?: string;
  styles?: object;
  className?: string;
  position?: object;
  properties?: Record<string, any>;
  children?: BackendAsset[];
  module?: string;
  companyId?: string;
  userId?: string;
  created?: string;
  modified?: string;
}

export class AssetService {
  private static cache: Map<string, BackendAsset> = new Map();

  /**
   * Fetch all assets from /api/assets
   */
  public static async fetchAssets(filter?: Record<string, any>): Promise<BackendAsset[]> {
    try {
      const authHeaders = AuthService.getInstance().getAuthHeaders();
      let url = '/api/assets';
      if (filter) {
        url += `?filter=${encodeURIComponent(JSON.stringify(filter))}`;
      }
      const response = await fetch(url, { headers: authHeaders });
      if (!response.ok) return [];
      const data = await response.json();
      const assets = Array.isArray(data) ? data : [];
      assets.forEach(ast => {
        if (ast.name) this.cache.set(ast.name, ast);
        if (ast.id) this.cache.set(ast.id, ast);
      });
      return assets;
    } catch (err) {
      console.warn('[AssetService] Failed to fetch assets:', err);
      return [];
    }
  }

  /**
   * Fetch a single asset by name or ID
   */
  public static async getAssetByName(name: string): Promise<BackendAsset | null> {
    if (this.cache.has(name)) {
      return this.cache.get(name)!;
    }
    const filter = { where: { or: [{ name }, { id: name }] } };
    const assets = await this.fetchAssets(filter);
    return assets.length > 0 ? assets[0] : null;
  }

  /**
   * Create a new asset in the database
   */
  public static async createAsset(asset: BackendAsset): Promise<BackendAsset | null> {
    try {
      const authHeaders = {
        ...AuthService.getInstance().getAuthHeaders(),
        'Content-Type': 'application/json'
      };
      const response = await fetch('/api/assets', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(asset)
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const created = await response.json();
      if (created.name) this.cache.set(created.name, created);
      if (created.id) this.cache.set(created.id, created);
      return created;
    } catch (err) {
      console.error('[AssetService] Create asset failed:', err);
      return null;
    }
  }

  /**
   * Update an existing asset by ID
   */
  public static async updateAsset(id: string, asset: Partial<BackendAsset>): Promise<BackendAsset | null> {
    try {
      const authHeaders = {
        ...AuthService.getInstance().getAuthHeaders(),
        'Content-Type': 'application/json'
      };
      const response = await fetch(`/api/assets/${id}`, {
        method: 'PATCH',
        headers: authHeaders,
        body: JSON.stringify(asset)
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const updated = await response.json().catch(() => ({ id, ...asset }));
      if (updated.name) this.cache.set(updated.name, updated as BackendAsset);
      if (id) this.cache.set(id, updated as BackendAsset);
      return updated as BackendAsset;
    } catch (err) {
      console.error('[AssetService] Update asset failed:', err);
      return null;
    }
  }

  /**
   * Seed core default assets into /api/assets database if missing
   */
  public static async seedDefaultAssets(): Promise<BackendAsset[]> {
    const existing = await this.fetchAssets();
    const defaultSeeds: BackendAsset[] = [
      {
        name: 'king_warrior',
        category: 'Graphic',
        type: 'component',
        properties: {
          geometry: { torsoWidth: 80, torsoHeight: 110, headRadiusX: 38, headRadiusY: 46, armWidth: 18, armLength: 52, legWidth: 22, legLength: 55, shoeColor: '#451a03' },
          colors: { skin: '#f87171', hair: '#1e293b', shirt: '#dc2626', pant: '#7f1d1d', accent: '#f59e0b' },
          accessories: { crown: true },
          voice: { gender: 'male', pitch: 0.8, rate: 0.95 },
          scale: 1.2
        }
      },
      {
        name: 'teacher_rig',
        category: 'Graphic',
        type: 'component',
        properties: {
          geometry: { torsoWidth: 70, torsoHeight: 100, headRadiusX: 34, headRadiusY: 40, armWidth: 16, armLength: 48, legWidth: 20, legLength: 50 },
          colors: { skin: '#fde047', hair: '#0f172a', shirt: '#0284c7', pant: '#1e293b', accent: '#fbbf24' },
          accessories: { glasses: true },
          voice: { gender: 'male', pitch: 1.0, rate: 1.0 }
        }
      },
      {
        name: 'student_rig',
        category: 'Graphic',
        type: 'component',
        properties: {
          geometry: { torsoWidth: 65, torsoHeight: 90, headRadiusX: 32, headRadiusY: 38, armWidth: 14, armLength: 44, legWidth: 18, legLength: 46 },
          colors: { skin: '#38bdf8', hair: '#475569', shirt: '#10b981', pant: '#334155', accent: '#34d399' },
          accessories: { cap: true },
          voice: { gender: 'female', pitch: 1.2, rate: 1.0 }
        }
      },
      {
        name: 'warhorse',
        category: 'Graphic',
        type: 'image',
        src: 'https://images.unsplash.com/photo-1553284965-83fd3e82fa5a?auto=format&fit=crop&w=400&q=80',
        properties: { speed: 1.2, category: 'mount' }
      },
      {
        name: 'fort_battleground',
        category: 'Background',
        type: 'html',
        properties: { theme: 'sunset', weather: 'dusty' }
      },
      {
        name: 'palace_hall',
        category: 'Background',
        type: 'html',
        properties: { theme: 'regal', light: 'golden' }
      },
      {
        name: 'classroom_env',
        category: 'Background',
        type: 'html',
        properties: { theme: 'academic', light: 'bright' }
      },
      {
        name: 'chalkboard_widget',
        category: 'Component',
        type: 'element',
        properties: { width: 1720, height: 940, theme: 'green' }
      },
      {
        name: 'text_heading',
        category: 'Text',
        type: 'element',
        properties: { text: 'Title Heading', fontSize: 32, fill: '#fde047' }
      },
      {
        name: 'graphic_shape',
        category: 'Graphic',
        type: 'element',
        properties: { kind: 'circle', radius: 40, fill: '#38bdf8' }
      },
      {
        name: 'particle_emitter',
        category: 'Component',
        type: 'component',
        properties: { effect: 'sparkles', count: 30 }
      },
      {
        name: 'quiz_widget',
        category: 'Component',
        type: 'element',
        properties: { question: 'Sample Question', options: ['Option A', 'Option B'] }
      }
    ];

    for (const seed of defaultSeeds) {
      if (!existing.some(a => a.name === seed.name)) {
        await this.createAsset(seed);
      }
    }

    return this.fetchAssets();
  }

  /**
   * Synchronously retrieve asset from cache
   */
  public static getCachedAsset(nameOrId: string): BackendAsset | null {
    return this.cache.get(nameOrId) || null;
  }
}
